import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '../../pages/OrdersList';
import { SkeletonList } from '../ui/Skeleton';
import { formatDate, formatEUR } from '../../lib/format';
import { DashboardPanel, PanelEmpty } from './DashboardPanel';

export interface RecentOrderRow {
  id: string;
  order_number?: string;
  status: string;
  total_ttc?: string | number;
  customer_id?: string | null;
  customer_name?: string | null;
  created_at?: string;
}

/**
 * Dashboard panel — the newest orders across every channel. Rows link through to the order,
 * so this doubles as the fastest route from the dashboard into today's work.
 */
export function RecentOrdersPanel({
  rows,
  loading,
  limit = 5,
}: {
  rows: RecentOrderRow[] | null;
  loading?: boolean;
  limit?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const visible = (rows ?? []).slice(0, limit);

  return (
    <DashboardPanel title={t('dashboard.recentOrders')} viewAllHref="/orders">
      {loading ? (
        <div className="p-5">
          <SkeletonList rows={limit} />
        </div>
      ) : visible.length === 0 ? (
        <PanelEmpty>{t('dashboard.noOrders')}</PanelEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="text-left font-medium px-5 py-2">{t('dashboard.orderNo')}</th>
                <th className="text-left font-medium px-5 py-2">{t('common.customer')}</th>
                <th className="text-left font-medium px-5 py-2">{t('common.status')}</th>
                <th className="text-right font-medium px-5 py-2">{t('common.ttc')}</th>
                <th className="text-right font-medium px-5 py-2">{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/orders/${r.id}`)}
                  className="border-t cursor-pointer hover:bg-muted/50"
                >
                  <td className="px-5 py-3 font-semibold whitespace-nowrap">
                    {r.order_number ?? r.id.slice(0, 8)}
                  </td>
                  {/* A POS walk-in carries no customer record — it is counter business. */}
                  <td className="px-5 py-3 text-muted-foreground">
                    {r.customer_name ?? t('dashboard.counter')}
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill s={r.status} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums whitespace-nowrap">
                    {formatEUR(r.total_ttc)}
                  </td>
                  <td className="px-5 py-3 text-right text-muted-foreground whitespace-nowrap">
                    {formatDate(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardPanel>
  );
}
